import { useAtomValue } from 'jotai';
import { agentPermissionsAtom } from '@/stores/role';
import { useMemo } from 'react';

/**
 * 权限检查Hook
 * 提供统一的权限检查方法
 */
export const usePermissions = () => {
  const { agentPermissions, isLoading } = useAtomValue(agentPermissionsAtom);

  // 缓存权限数据，避免重复计算
  const permissionsData = useMemo(() => {
    if (!agentPermissions?.mPerms) {
      return {
        allPermissions: [],
        modulePermissions: {},
      };
    }

    // 将所有模块权限扁平化为全局权限列表
    const allPermissions = Object.values(agentPermissions.mPerms).flat();
    const modulePermissions = agentPermissions.mPerms;

    return {
      allPermissions,
      modulePermissions,
    };
  }, [agentPermissions]);

  /**
   * 检查是否有指定权限（支持 module:permission 格式和直接 permission 格式）
   * @param permission 权限标识
   * @returns 是否有权限
   */
  const hasPermission = (permission: string): boolean => {
    // 如果权限数据还未加载，默认返回 false
    if (!agentPermissions?.mPerms) {
      return false;
    }

    // 检查全局权限列表（所有模块权限的扁平化）
    return permissionsData.allPermissions.includes(permission);
  };

  /**
   * 检查是否有指定模块的指定权限
   * @param moduleId 模块ID
   * @param permission 权限标识
   * @returns 是否有权限
   */
  const hasModulePermission = (moduleId: string, permission: string): boolean => {
    // 如果权限数据还未加载，默认返回 false
    if (!agentPermissions?.mPerms) {
      return false;
    }

    const modulePerms = permissionsData.modulePermissions[moduleId];
    return modulePerms?.includes(permission) || false;
  };

  /**
   * 获取指定模块的所有权限
   * @param moduleId 模块ID
   * @returns 权限列表
   */
  const getModulePermissions = (moduleId: string): string[] => {
    return permissionsData.modulePermissions[moduleId] || [];
  };

  /**
   * 获取所有权限列表
   * @returns 所有权限列表
   */
  const getAllPermissions = (): string[] => {
    return permissionsData.allPermissions;
  };

  /**
   * 检查是否有多个权限中的任意一个
   * @param permissions 权限列表
   * @returns 是否有任意一个权限
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some((permission) => hasPermission(permission));
  };

  /**
   * 检查是否同时拥有多个权限
   * @param permissions 权限列表
   * @returns 是否同时拥有所有权限
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every((permission) => hasPermission(permission));
  };

  return {
    // 原始数据
    agentPermissions,
    isLoading,

    // 基础权限检查
    hasPermission,
    hasModulePermission,

    // 批量权限检查
    hasAnyPermission,
    hasAllPermissions,

    // 数据获取
    getModulePermissions,
    getAllPermissions,

    // 具体业务权限检查 - 统一使用 hasPermission 方法
    canViewIm: () => hasPermission('im:viewPage'),
    canReplyCustomer: () => hasPermission('im:replyCustomer'),
    canEditCustomerInfo: () => hasPermission('im:editCustomerInfo'),
    canManageCustomerTransfer: () => hasPermission('im:manageCustomerTransfer'),

    canViewChannelAccount: () => hasPermission('channelAccount:viewPage'),
    canEditChannelSettings: () => hasPermission('channelAccount:settingsEdit'),
    canManageChannelBind: () => hasPermission('channelAccount:bindManage'),

    canViewStatistics: () => hasPermission('statistics:viewPage'),
    canExportReport: () => hasPermission('statistics:exportReport'),

    canViewTag: () => hasPermission('tag:viewPage'),
    canEditTag: () => hasPermission('tag:settingsEdit'),

    canViewAutomation: () => hasPermission('automation:viewPage'),
    canEditAutomation: () => hasPermission('automation:edit'),

    canViewPointManagement: () => hasPermission('pointManagement:viewPage'),
  };
};
